import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';

const ConnectionStatusBadge = () => {
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    checkConnection();
    
    // Check connection every 30 seconds
    const interval = setInterval(() => {
      checkConnection();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const checkConnection = async () => {
    try {
      // Check if supabase client is initialized
      if (!supabase) {
        setConnectionStatus('disconnected');
        logger?.error('Supabase client not initialized');
        setLastChecked(new Date());
        return;
      }

      // Use a simple health check that doesn't depend on RLS or auth state
      // Try to get the current session - this is a lightweight operation
      const { data, error } = await supabase?.auth?.getSession();

      if (error) {
        // If there's an error getting session, check if it's a network issue
        if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
          setConnectionStatus('disconnected');
          logger?.error('Network connectivity issue', error);
        } else {
          // Other errors might not indicate disconnection
          setConnectionStatus('connected');
        }
      } else {
        // Successfully communicated with Supabase
        setConnectionStatus('connected');
      }
      setLastChecked(new Date());
    } catch (err) {
      // Catch any unexpected errors
      setConnectionStatus('disconnected');
      setLastChecked(new Date());
      logger?.error('Connection check error', err);
    }
  };

  const getStatusConfig = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: 'CheckCircle',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Connected',
          pulse: false
        };
      case 'disconnected':
        return {
          icon: 'XCircle',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Disconnected',
          pulse: false
        };
      case 'checking':
      default:
        return {
          icon: 'RefreshCw',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Checking',
          pulse: true
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${config?.bgColor} ${config?.borderColor}`}
      title={lastChecked ? `Last checked: ${lastChecked?.toLocaleTimeString()}` : 'Checking connection...'}
    >
      <Icon
        name={config?.icon}
        className={`w-4 h-4 ${config?.color} ${config?.pulse ? 'animate-spin' : ''}`}
      />
      <span className={`text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    </div>
  );
};

export default ConnectionStatusBadge;